import { FileText, X, Upload as UploadIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { ChangeEvent } from 'react'

interface UploadXmlModalProps {
  showUploadModal: boolean
  setShowUploadModal: (show: boolean) => void
  xmlFile: File | null
  setXmlFile: (file: File | null) => void
  handleXmlUpload: (e: ChangeEvent<HTMLInputElement>) => void
  uploadData: {
    paymentForm: string
    paymentMethod: string
    entryDate: string
  }
  setUploadData: (data: any) => void
  handleProcessXml: () => void
}

export function UploadXmlModal({
  showUploadModal,
  setShowUploadModal,
  xmlFile,
  setXmlFile,
  handleXmlUpload,
  uploadData,
  setUploadData,
  handleProcessXml,
}: UploadXmlModalProps) {
  return (
    <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Cargar Factura XML</DialogTitle>
          <DialogDescription className="sr-only">
            Cargar productos mediante archivo XML de factura
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* XML Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Archivo XML de Factura <span className="text-red-600">*</span>
            </label>
            <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition-colors hover:border-primary/40">
              <input
                type="file"
                accept=".xml"
                onChange={handleXmlUpload}
                className="hidden"
                id="xml-upload"
              />
              <label htmlFor="xml-upload" className="cursor-pointer">
                {xmlFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-8 w-8 text-primary" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-900">
                        {xmlFile.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {(xmlFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        setXmlFile(null)
                      }}
                      className="rounded p-1 hover:bg-slate-100"
                    >
                      <X className="h-4 w-4 text-slate-600" />
                    </button>
                  </div>
                ) : (
                  <>
                    <UploadIcon className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                    <p className="text-sm text-slate-600">
                      Haz clic para seleccionar archivo XML
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Formato CFDI 3.3 o 4.0
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Payment Form */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Forma de Pago <span className="text-red-600">*</span>
              </label>
              <Select
                value={uploadData.paymentForm}
                onValueChange={(value) =>
                  setUploadData({ ...uploadData, paymentForm: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar forma de pago" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contado">Contado</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Método de Pago <span className="text-red-600">*</span>
              </label>
              <Select
                value={uploadData.paymentMethod}
                onValueChange={(value) =>
                  setUploadData({ ...uploadData, paymentMethod: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar método" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">
                    Transferencia Bancaria
                  </SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="tarjeta">
                    Tarjeta de Crédito/Débito
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Entry Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Fecha de Ingreso <span className="text-red-600">*</span>
            </label>
            <Input
              type="date"
              value={new Date().toISOString().split('T')[0]}
              readOnly
              className="bg-slate-50"
            />
            <p className="text-xs text-slate-600">
              Fecha actual (no modificable)
            </p>
          </div>

          {/* Info Message */}
          <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
            <p className="mb-2 text-sm font-medium text-primary/90">
              Información:
            </p>
            <ul className="list-inside list-disc space-y-1 text-sm text-primary">
              <li>El sistema procesará automáticamente los productos del XML</li>
              <li>Se validará que los productos existan en el catálogo</li>
              <li>Se actualizará el inventario con las cantidades ingresadas</li>
              <li>Se generará un registro de compra con todos los detalles</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleProcessXml}
              disabled={
                !xmlFile ||
                !uploadData.paymentForm ||
                !uploadData.paymentMethod
              }
              className="flex-1"
            >
              Procesar Factura
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false)
                setXmlFile(null)
                setUploadData({
                  paymentForm: '',
                  paymentMethod: '',
                  entryDate: '',
                })
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
